import { useState } from 'react'
import {
  PiX,
  PiCaretDown,
  PiCaretUp,
  PiCheckCircle,
  PiWarningCircle,
} from 'react-icons/pi'
import { Z_INDEX } from '../../styles/z-index'
import { useDownload } from './DownloadContext'

export function DownloadWidget() {
  const { state, cancelDownload, clearDownload, isVisible } = useDownload()
  const [isCollapsed, setIsCollapsed] = useState(false)

  if (!isVisible) {
    return null
  }

  const getTitle = () => {
    const typeLabel = state.label || 'download'
    switch (state.status) {
      case 'pending':
        return 'Waiting to start...'
      case 'preparing':
        return 'Preparing download...'
      case 'generating':
        return `Creating ${typeLabel}...`
      case 'downloading':
        return `Downloading ${typeLabel}...`
      case 'completed':
        return 'Download complete'
      case 'failed':
        return 'Download failed'
      default:
        return 'Download'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <PiCheckCircle size={14} />
      case 'failed':
        return <PiWarningCircle size={14} />
      case 'processing':
        return <Spinner />
      default:
        return null
    }
  }

  const handleClose = () => {
    if (
      state.status === 'downloading' ||
      state.status === 'generating' ||
      state.status === 'preparing' ||
      state.status === 'pending'
    ) {
      cancelDownload()
    } else {
      clearDownload()
    }
  }

  const getHeaderBgColor = () => {
    if (state.status === 'failed') {
      return 'bg-red-500'
    }
    return 'bg-fb-green-900'
  }

  const getProgressBarColor = () => {
    if (state.status === 'failed') {
      return 'bg-red-500'
    }
    return 'bg-fb-green-700'
  }

  // Indeterminate progress bar for:
  // - pending status (Celery task hasn't started yet)
  // - preparing/generating without a requestId (synchronous download in progress)
  const isIndeterminate =
    state.status === 'pending' ||
    (!state.requestId &&
      (state.status === 'generating' || state.status === 'preparing'))

  const getItemStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-emerald-500'
      case 'failed':
        return 'text-red-500'
      default:
        return 'text-gray-400'
    }
  }

  return (
    <div
      className="fixed bottom-6 right-6 w-[360px] bg-white rounded-lg shadow-xl overflow-hidden"
      style={{ zIndex: Z_INDEX.NOTIFICATION - 10 }}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 text-white ${getHeaderBgColor()}`}
      >
        <div className="flex items-center gap-2 font-medium text-sm">
          {state.status === 'completed' && <PiCheckCircle size={18} />}
          {state.status === 'failed' && <PiWarningCircle size={18} />}
          {(state.status === 'downloading' ||
            state.status === 'generating' ||
            state.status === 'preparing' ||
            state.status === 'pending') && <Spinner />}
          {getTitle()}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 rounded hover:bg-white/20 transition-colors"
          >
            {isCollapsed ? <PiCaretUp size={18} /> : <PiCaretDown size={18} />}
          </button>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-white/20 transition-colors"
          >
            <PiX size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isCollapsed ? 'max-h-0' : 'max-h-[300px]'
        }`}
      >
        {/* Progress Section */}
        <div className="p-4 border-b border-gray-200">
          <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
            {isIndeterminate ? (
              <div
                className={`h-full w-1/2 ${getProgressBarColor()} animate-indeterminate`}
              />
            ) : (
              <div
                className={`h-full transition-all duration-300 ease-in-out ${getProgressBarColor()}`}
                style={{ width: `${state.progress}%` }}
              />
            )}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>
              {state.status === 'pending'
                ? 'Waiting to start...'
                : isIndeterminate
                  ? 'Processing...'
                  : `${state.progress}% complete`}
            </span>
            <span>
              {state.completedCount} of {state.totalCount} items
            </span>
          </div>
        </div>

        {/* Error Message */}
        {state.errorMessage && (
          <div className="px-4 py-3 bg-red-50 text-red-600 text-sm border-t border-red-200">
            {state.errorMessage}
          </div>
        )}

        {/* Items List */}
        <div className="max-h-[200px] overflow-y-auto py-2">
          {state.items.map((item) => (
            <div
              key={item.itemId}
              className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className="flex-1 truncate mr-2" title={item.itemName}>
                {item.itemName}
              </span>
              <span
                className={`flex items-center ${getItemStatusColor(item.status)}`}
              >
                {getStatusIcon(item.status)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Spinner() {
  return (
    <div className="w-[14px] h-[14px] border-2 border-gray-200 border-t-fb-green-700 rounded-full animate-spin" />
  )
}
